import { useMemo } from "react";
import { Grid, Typography } from "@material-ui/core";

import {
	fieldOptionListVariableName,
	useCalculationField,
	useCalculationFields,
	useDispatch,
	useTemplateDebugManager,
	useVariableData
} from "@ploy-lib/calculation";
import { DploySelectionField } from "@ploy-ui/form-fields";
import { FormattedNumber, defineMessages, useIntl } from "react-intl";
import { useField } from "formik";
import { SectionProps } from "../../section";
import { addRegisteredSectionLayout } from "../../section/sectionLayoutDescriptions";
import {
	ExtendedApplicationDebt,
	ensureCollectionHasIds
} from "./LimitedDebtField";

import { ApplicationDebt, ApplicationDebtSource } from "@ploy-lib/types";

const messages = defineMessages({
	whatLoansToFulfill: {
		id: "template-form.field.limitedDebtFulfillment.whatLoansToFulfill",
		defaultMessage: "Which loans should be fulfilled or paid down"
	},
	loanFrom: {
		id: "template-form.field.limitedDebtFulfillment.loanFrom",
		defaultMessage: "{loanType} from {lender}"
	},
	validAccountNumberisRequired: {
		id: "template-form.field.limitedDebtFulfillment.validAccountNumberisRequired",
		defaultMessage: "A valid bank account number is required"
	},
	loanNumber: {
		id: "template-form.field.limitedDebtFulfillment.loanNumber",
		defaultMessage: "Account number"
	}
});

const LabelComponent = ({ debt }: { debt: ExtendedApplicationDebt }) => {
	const intl = useIntl();
	const loanTypes = (useVariableData(
		"Calculator",
		fieldOptionListVariableName("LoanTypeOptionHelper")
	).value ?? []) as { key: string; value: string }[];
	const localizedLoanType =
		loanTypes.find(l => l.key === debt.loanType)?.value ?? debt.loanType;
	return (
		<Grid container spacing={2} direction="column">
			<Grid
				container
				item
				spacing={1}
				wrap="nowrap"
				justifyContent="space-between"
			>
				<Grid item>
					<Typography style={{ fontWeight: "bold" }}>
						{intl.formatMessage(messages.loanFrom, {
							loanType: localizedLoanType,
							lender: debt.lender
						})}
					</Typography>
				</Grid>
				<Grid item style={{ flex: "none" }}>
					<Typography noWrap style={{ fontWeight: "bold", textAlign: "right" }}>
						<FormattedNumber value={debt.amount} format="currency" />
					</Typography>
				</Grid>
			</Grid>
			<Grid
				container
				item
				spacing={1}
				wrap="nowrap"
				justifyContent="space-between"
			>
				<Grid item>
					<Typography>{intl.formatMessage(messages.loanNumber)}</Typography>
				</Grid>
				<Grid item style={{ flex: "none" }}>
					<Typography>{debt.loanNumber}</Typography>
				</Grid>
			</Grid>
		</Grid>
	);
};

export function LimitedDebtsFulfillmentSection(props: SectionProps) {
	const intl = useIntl();
	const dispatch = useDispatch();
	const debtsField = useCalculationField<ApplicationDebt[] | undefined>({
		namespace: "Calculator",
		name: "Debts"
	});

	const debtList = useMemo(
		() => ensureCollectionHasIds(debtsField?.value),
		[debtsField?.value]
	);

	const creditPurposeField = useCalculationField<string[] | undefined>({
		namespace: "PRIVATE_CUSTOMER_DATA",
		name: "CreditPurpose"
	});
	const creditPurposeValue = creditPurposeField?.value ?? [];

	const relevantDebts = useMemo(
		() =>
			debtList.filter(
				d =>
					d.loanType != null &&
					// Filter out loans from debt registry because we do not know their origin
					d.source !== ApplicationDebtSource.DebtRegistry &&
					// Also filter out existing customer engagement from backoffice
					d.source !== ApplicationDebtSource.CustomerEngagement
			) ?? [],
		[debtList]
	);

	const { enabled: debugEnabled } = useTemplateDebugManager();

	if (!creditPurposeValue.includes("REFINANCING")) {
		return <></>;
	}

	if (relevantDebts.length === 0) {
		return debugEnabled ? (
			<Grid
				container
				spacing={1}
				className={props.className}
				onClick={props.onClick}
			>
				<Grid item xs>
					<Typography>No content here, just helping debugging :)</Typography>
				</Grid>
			</Grid>
		) : null;
	}

	const relevantDebtsKeyValue = relevantDebts.map(d => {
		return {
			key: d._frontendOnlyId ?? d.applicationDebtId,
			value: <LabelComponent debt={d} />
		};
	});

	const selectedDebtsIds = relevantDebts
		.filter(d => d.refinance)
		.map(d => d._frontendOnlyId ?? d.applicationDebtId);

	return (
		<Grid
			container
			spacing={1}
			className={props.className}
			onClick={props.onClick}
		>
			<Grid item xs={12}>
				<Typography variant="body2" style={{ fontWeight: "bold" }}>
					{intl.formatMessage(messages.whatLoansToFulfill)}
				</Typography>
			</Grid>
			<DploySelectionField
				items={relevantDebtsKeyValue}
				fullWidth={true}
				value={selectedDebtsIds}
				multiple={true}
				onChange={selectedValue => {
					const updatedList = relevantDebts.map(rd => {
						if (
							selectedValue.includes(rd._frontendOnlyId ?? rd.applicationDebtId)
						) {
							rd.refinance = true;
						} else rd.refinance = false;
						return rd;
					});
					dispatch({
						type: "patch",
						payload: {
							patches: [
								{
									target: "Debts",
									namespace: "Calculator",
									value: updatedList,
									overwrite: true,
									changeTrigger: "Debts_manualSave"
								}
							]
						}
					});
				}}
			/>
		</Grid>
	);
}

addRegisteredSectionLayout({
	displayName: "LimitedDebtsFulfillmentSection",
	name: "LimitedDebtsFulfillmentSection",
	settings: {}
});

LimitedDebtsFulfillmentSection.displayName = "LimitedDebtFulfillment";
